import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Flex,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { DateTime } from "luxon";

export interface HolidayItem {
  _id: string;
  name: string;
  date: string; // YYYY-MM-DD
  type?: string;
}

/** Display label + colour per Holiday Master type; shared with Holiday Master. */
export const HOLIDAY_TYPE_META: Record<string, { label: string; color: string }> = {
  national: { label: "National", color: "green" },
  optional: { label: "Optional", color: "purple" },
  weekly_off: { label: "Weekly Off", color: "blue" },
  restricted: { label: "Restricted", color: "orange" },
};

const typeMeta = (type?: string) =>
  HOLIDAY_TYPE_META[type || "national"] || { label: type || "", color: "gray" };

interface HolidayCalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
  holidays: HolidayItem[];
}

/**
 * Year-at-a-glance holiday calendar: one compact card per month, a type
 * filter with counts, and the next upcoming holiday called out. Sized to fit
 * a laptop screen; the body scrolls on its own if it has to.
 */
export const HolidayCalendarModal = ({
  isOpen,
  onClose,
  holidays,
}: HolidayCalendarModalProps) => {
  const today = DateTime.now().toISODate() as string;
  const currentYear = DateTime.now().year;

  const years = useMemo(() => {
    const set = new Set(holidays.map((h) => Number(h.date.slice(0, 4))));
    set.add(currentYear);
    return Array.from(set).sort((a, b) => a - b);
  }, [holidays, currentYear]);

  const [year, setYear] = useState(currentYear);
  const [typeFilter, setTypeFilter] = useState("all");

  useEffect(() => {
    if (isOpen) {
      setYear(currentYear);
      setTypeFilter("all");
    }
  }, [isOpen, currentYear]);

  const yearHolidays = useMemo(
    () =>
      holidays
        .filter((h) => h.date.startsWith(`${year}-`))
        .sort((a, b) => a.date.localeCompare(b.date)),
    [holidays, year],
  );

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    yearHolidays.forEach((h) => {
      const key = h.type || "national";
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [yearHolidays]);

  const visible = yearHolidays.filter(
    (h) => typeFilter === "all" || (h.type || "national") === typeFilter,
  );
  const nextHoliday = visible.find((h) => h.date >= today);

  const months = Array.from({ length: 12 }, (_, i) => {
    const prefix = `${year}-${String(i + 1).padStart(2, "0")}-`;
    return {
      label: DateTime.fromObject({ year, month: i + 1 }).toFormat("MMMM"),
      items: visible.filter((h) => h.date.startsWith(prefix)),
    };
  }).filter((m) => m.items.length > 0);

  const yearIndex = years.indexOf(year);

  // Open on the current month rather than January, so upcoming holidays are
  // in view without scrolling.
  const currentMonthLabel = DateTime.now().toFormat("MMMM");
  const monthRefs = useRef<Record<string, HTMLDivElement | null>>({});
  useEffect(() => {
    if (!isOpen || year !== currentYear) return;
    const timer = setTimeout(() => {
      const target =
        months.find((m) => m.items.some((h) => h.date >= today)) || months[0];
      const el = target && monthRefs.current[target.label];
      el?.scrollIntoView({ block: "start" });
    }, 50);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, year, typeFilter, months.length]);

  const filterChip = (key: string, label: string, count: number, color?: string) => {
    const active = typeFilter === key;
    return (
      <Flex
        key={key}
        as="button"
        type="button"
        align="center"
        gap={2}
        px={3}
        py={1}
        borderRadius="full"
        border="1px solid"
        borderColor={active ? "#327171" : "gray.200"}
        bg={active ? "#327171" : "white"}
        color={active ? "white" : "gray.700"}
        fontSize="13px"
        fontWeight="600"
        onClick={() => setTypeFilter(key)}
        _hover={{ borderColor: "#327171" }}
      >
        {color && (
          <Box w="8px" h="8px" borderRadius="full" bg={`${color}.400`} />
        )}
        {label}
        <Text as="span" opacity={0.75} fontWeight="500" m={0}>
          {count}
        </Text>
      </Flex>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="6xl"
      isCentered
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent maxH="88vh" borderRadius="14px">
        <ModalHeader pb={2}>
          <Flex align="center" gap={2} pr={8}>
            <Text as="span" fontSize="20px" fontWeight="700" m={0}>
              Holiday Calendar
            </Text>
            <Flex align="center" ml="auto" gap={1}>
              <IconButton
                aria-label="Previous year"
                icon={<ChevronLeftIcon boxSize={5} />}
                size="sm"
                variant="ghost"
                isDisabled={yearIndex <= 0}
                onClick={() => setYear(years[yearIndex - 1])}
              />
              <Text as="span" fontSize="16px" fontWeight="700" color="#327171" m={0}>
                {year}
              </Text>
              <IconButton
                aria-label="Next year"
                icon={<ChevronRightIcon boxSize={5} />}
                size="sm"
                variant="ghost"
                isDisabled={yearIndex >= years.length - 1}
                onClick={() => setYear(years[yearIndex + 1])}
              />
            </Flex>
          </Flex>

          <Flex mt={3} gap={2} wrap="wrap" align="center">
            {filterChip("all", "All", yearHolidays.length)}
            {Object.entries(HOLIDAY_TYPE_META)
              .filter(([key]) => typeCounts[key])
              .map(([key, meta]) =>
                filterChip(key, meta.label, typeCounts[key], meta.color),
              )}
            {typeCounts.optional ? (
              <Text fontSize="12px" color="gray.500" fontWeight="500" m={0} ml={1}>
                Optional: pick any 2 in the year
              </Text>
            ) : null}
            {nextHoliday && (
              <Flex
                ml={{ base: 0, md: "auto" }}
                px={3}
                py={1}
                borderRadius="full"
                bg="#e8f3f3"
                align="center"
                gap={1.5}
                fontSize="13px"
                fontWeight="500"
              >
                <Text as="span" color="#327171" fontWeight="700" m={0}>
                  Next:
                </Text>
                <Text as="span" m={0}>
                  {nextHoliday.name},{" "}
                  {DateTime.fromISO(nextHoliday.date).toFormat("ccc dd LLL")}
                </Text>
                <Text as="span" color="gray.500" m={0}>
                  {nextHoliday.date === today
                    ? "(today)"
                    : `(${DateTime.fromISO(nextHoliday.date).toRelative({
                        base: DateTime.fromISO(today),
                        unit: "days",
                      })})`}
                </Text>
              </Flex>
            )}
          </Flex>

        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pb={6}>
          {months.length === 0 ? (
            <Text color="gray.500" fontSize="sm" py={8} textAlign="center">
              No holidays configured for {year}.
            </Text>
          ) : (
            <SimpleGrid
              columns={{ base: 1, sm: 2, lg: 3, xl: 4 }}
              spacing={3}
              alignItems="start"
            >
              {months.map((month) => (
                <Box
                  key={month.label}
                  ref={(el: HTMLDivElement | null) => {
                    monthRefs.current[month.label] = el;
                  }}
                  scrollMarginTop="8px"
                  border="1px solid"
                  borderColor={
                    year === currentYear && month.label === currentMonthLabel
                      ? "#327171"
                      : "#e2e8f0"
                  }
                  borderRadius="12px"
                  px={3}
                  py={2.5}
                  bg="white"
                >
                  <Flex justify="space-between" align="center" mb={1}>
                    <Text
                      fontSize="12px"
                      fontWeight="700"
                      letterSpacing="0.06em"
                      textTransform="uppercase"
                      color="#327171"
                      m={0}
                    >
                      {month.label}
                    </Text>
                    <Text fontSize="12px" color="gray.400" m={0}>
                      {month.items.length}
                    </Text>
                  </Flex>

                  {month.items.map((h) => {
                    const date = DateTime.fromISO(h.date);
                    const meta = typeMeta(h.type);
                    const isPast = h.date < today;
                    const isNext = nextHoliday?._id === h._id;
                    return (
                      <Flex
                        key={h._id}
                        align="center"
                        gap={2.5}
                        py={1}
                        px={1.5}
                        mx={-1.5}
                        borderRadius="6px"
                        bg={isNext ? "#e8f3f3" : undefined}
                        opacity={isPast ? 0.55 : 1}
                      >
                        <Text
                          w="52px"
                          flexShrink={0}
                          fontSize="13px"
                          fontWeight="700"
                          m={0}
                          whiteSpace="nowrap"
                        >
                          {date.toFormat("dd")}{" "}
                          <Text as="span" fontSize="11px" fontWeight="500" color="gray.500">
                            {date.toFormat("ccc")}
                          </Text>
                        </Text>
                        <Text
                          flex={1}
                          minW={0}
                          fontSize="13px"
                          fontWeight="500"
                          noOfLines={1}
                          title={h.name}
                          m={0}
                        >
                          {h.name}
                        </Text>
                        <Tooltip label={meta.label} hasArrow placement="left">
                          <Box
                            w="8px"
                            h="8px"
                            flexShrink={0}
                            borderRadius="full"
                            bg={`${meta.color}.400`}
                          />
                        </Tooltip>
                      </Flex>
                    );
                  })}
                </Box>
              ))}
            </SimpleGrid>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
